import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Heart, MapPin, Clock, ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { useToast } from '@/hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DealCard = ({ deal, onSaveToggle }) => {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [isSaved, setIsSaved] = useState(
    user?.saved_deals?.includes(deal.id) || false
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please login to save deals",
        variant: "destructive"
      });
      return;
    }

    setSaving(true);
    try {
      if (isSaved) {
        await api.delete(`/users/${user.id}/save-deal/${deal.id}`);
        setIsSaved(false);
        toast({ title: "Deal removed from saved" });
      } else {
        await api.post(`/users/${user.id}/save-deal/${deal.id}`);
        setIsSaved(true);
        toast({ title: "Deal saved!" });
      }
      if (onSaveToggle) onSaveToggle();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save deal",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleViewDeal = async (e) => {
    e.stopPropagation(); // Prevent parent Link from capturing the click
    // Don't prevent default - let the anchor tag open normally
    try {
      // Track click asynchronously
      api.post(`/deals/${deal.id}/click`).catch(err => 
        console.error('Failed to track click:', err)
      );
    } catch (error) {
      console.error('Failed to track click:', error);
    }
    // Let the anchor tag handle the navigation
  };

  const getTimeRemaining = () => {
    const now = new Date();
    const expires = new Date(deal.expires_at);
    const diff = expires - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days} days left`;
    if (hours > 0) return `${hours} hours left`;
    return 'Ending soon';
  };

  const getProgressValue = () => {
    const now = new Date();
    const start = new Date(deal.starts_at);
    const end = new Date(deal.expires_at);
    const total = end - start;
    const elapsed = now - start;
    return Math.max(0, Math.min(100, (elapsed / total) * 100));
  };

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300 group" data-testid="deal-card">
      <Link to={`/deals/${deal.id}`}>
      <div className="relative">
        <img
          src={deal.image_url}
          alt={deal.title}
          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
        <div className="absolute top-2 right-2 flex gap-2">
          <Badge className="bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="discount-badge">
            {deal.discount_percentage}% OFF
          </Badge>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-white rounded-full p-2 shadow-md hover:bg-gray-100 transition-colors"
            data-testid="save-deal-button"
          >
            <Heart
              size={20}
              className={isSaved ? 'fill-red-500 text-red-500' : 'text-gray-600'}
            />
          </button>
        </div>
        {deal.is_featured && (
          <Badge className="absolute top-2 left-2 bg-yellow-500 text-black" data-testid="featured-badge">
            ⭐ Featured
          </Badge>
        )}
      </div>

      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <h3 className="font-semibold text-lg line-clamp-2" data-testid="deal-title">
              {deal.title}
            </h3>
            <p className="text-sm text-gray-600" data-testid="brand-name">{deal.brand_name}</p>
          </div>
        </div>

        <p className="text-sm text-gray-700 line-clamp-2" data-testid="deal-description">
          {deal.description}
        </p>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <MapPin size={16} />
          <span data-testid="deal-locations">{deal.locations.join(', ')}</span>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-emerald-600" data-testid="discounted-price">
                AED {deal.discounted_price}
              </span>
              <span className="text-sm text-gray-500 line-through" data-testid="original-price">
                AED {deal.original_price}
              </span>
            </div>
            <p className="text-xs text-gray-500" data-testid="savings-amount">
              Save AED {(deal.original_price - deal.discounted_price).toFixed(2)}
            </p>
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            {/* Only show time remaining for non-property deals */}
            {deal.category_name !== 'Property' ? (
              <span className="flex items-center gap-1 text-orange-600">
                <Clock size={14} />
                <span data-testid="time-remaining">{getTimeRemaining()}</span>
              </span>
            ) : (
              <span className="text-gray-500 text-xs">
                Listed on Property Finder
              </span>
            )}
            <span className="text-gray-500" data-testid="category-name">{deal.category_name}</span>
          </div>
          {/* Only show progress bar for non-property deals */}
          {deal.category_name !== 'Property' && (
            <Progress value={getProgressValue()} className="h-1" data-testid="time-progress" />
          )}
        </div>

        <a
          href={deal.deal_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleViewDeal}
          className="block w-full"
          data-testid="view-deal-link"
        >
          <Button
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="view-deal-button"
          >
            View Deal <ExternalLink size={16} className="ml-2" />
          </Button>
        </a>
      </CardContent>
      </Link>
    </Card>
  );
};
